// ProductForm.jsx — formulaire d'ajout / édition.
// Deux modes : « Étapes » (wizard Identité → Stock → Prix → Vérifier)
// et « Page » (tout sur une page). Sélecteurs en feuilles montantes,
// marge calculée en direct. Champs alignés sur le vrai modal GestionProduit.
import React, { useState, useRef, useEffect } from "react";
import {
  Ico, useIcons, fmt, genCode, genSlug, parseProductNumber,
  TYPE_OPTS, STOCK_MODE_OPTS, BARCODE_OPTS,
} from "./productsShared";
import { CATS, UNITS, WHS, TAXES } from "./productData";
import { OptionSheet } from "./ProductsSheets";
import VariantEditorMobile from "./VariantEditorMobile";
import API_CONFIG from "../../config/api";
import { compressImage } from "../../utils/imageUrlHelper";

// Résout l'URL d'affichage d'une image produit existante (édition).
// Défini ici car productsShared.js doit rester sans dépendance hors React.
const resolveImg = (img) =>
  !img
    ? null
    : String(img).startsWith("http")
    ? img
    : `${API_CONFIG.BASE_URL}${String(img).startsWith("/") ? "" : "/"}${img}`;

const STEPS = [
  { key: "id", label: "Identité" },
  { key: "stock", label: "Stock" },
  { key: "price", label: "Prix" },
];

function blankForm(references = {}) {
  const warehouses = references.warehouses || WHS;
  const taxes = references.taxes || TAXES;
  return {
    name: "", code: "", cat: "", type: "Produits finis", wh: warehouses[0]?.name || "",
    stockMode: "standard", desc: "", slug: "", bcSymbol: "EAN13", bc: "",
    stock: "", unit: "", alert: "", cost: "", sale: "", tax: taxes[0]?.name || "",
    pkLabel: "", pkContent: "", pkPrice: "",
    image: "", imageFile: null, removeImage: false,
  };
}
function formFromProduct(p, references = {}) {
  const units = references.units || UNITS;
  if (!p) return blankForm(references);
  return {
    ...blankForm(references),
    name: p.name, code: p.code, cat: p.cat, type: p.type || "Produits finis",
    unit: (units.find((u) => u.short === p.unit) || {}).name || p.unit || "",
    wh: p.wh || "", stockMode: p.stockMode || "standard",
    tax: p.tax || "", desc: p.desc || "", slug: p.slug || "", bcSymbol: p.bcSymbol || "EAN13",
    pkLabel: (p.pk && p.pk.label) || "", pkContent: p.pk ? String(p.pk.content) : "",
    pkPrice: p.pk ? String(p.pk.price) : "", stock: String(p.stock),
    alert: String(p.alert), cost: String(p.cost), sale: String(p.sale), bc: p.bc,
    image: p.image || "",
  };
}

/* ---- petits composants de champ ---- */
function Field({ label, req, hint, children }) {
  return (
    <div className="field">
      <div className="field__l">
        {label}
        {req ? <span className="req">*</span> : null}
        {hint ? <span className="field__hint">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}
function Text({ value, onChange, placeholder, mode = "text", suffix, disabled = false }) {
  if (suffix) {
    return (
      <div className="input-wrap">
        <input className="ctrl" inputMode={mode} placeholder={placeholder}
          value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        <span className="suffix">{suffix}</span>
      </div>
    );
  }
  return (
    <input className="ctrl" inputMode={mode} placeholder={placeholder}
      value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
  );
}
function Seg({ value, opts, onChange, disabled = false }) {
  return (
    <div className="seg">
      {opts.map((o) => (
        <button
          key={o.v}
          className={value === o.v ? "on" : ""}
          disabled={disabled}
          onClick={() => onChange(o.v)}
        >
          {o.ico ? <Ico name={o.ico} /> : null}{o.label}
        </button>
      ))}
    </div>
  );
}
function SelectBtn({ value, placeholder, leadIco, onOpen, disabled = false }) {
  return (
    <button
      className={"ctrl selbtn " + (value ? "" : "placeholder")}
      disabled={disabled}
      onClick={onOpen}
    >
      <span className="pre">
        {leadIco ? <Ico name={leadIco} /> : null}<span>{value || placeholder}</span>
      </span>
      <Ico name="chevron-down" className="chev" />
    </button>
  );
}

export default function ProductForm({
  product,
  onClose,
  onSave,
  onSaveVariants,
  toast,
  references = {},
  saving = false,
  isVariantsEligible = false,
  referenceNotice,
}) {
  const editing = !!product;
  const categories = references.categories || CATS;
  const units = references.units || UNITS;
  const warehouses = references.warehouses || WHS;
  const taxes = references.taxes || TAXES;
  const [form, setForm] = useState(() => formFromProduct(product, references));
  const isRawMaterial = form.type === "Matières premières";
  const priceLabel = isRawMaterial ? "Prix d’entrée en production" : "Prix de vente";
  const [view, setView] = useState("wizard");
  const [step, setStep] = useState(0);
  const [sheet, setSheet] = useState(null); // 'cat'|'unit'|'wh'|'tax'|null
  // Mode variantes (déclinaisons) — création ou conversion d'un produit fini.
  const [variantMode, setVariantMode] = useState(false);
  const [variantAxes, setVariantAxes] = useState([{ name: "Taille", values: [] }]);
  const [variantRows, setVariantRows] = useState([]);
  const [existingVariantKey, setExistingVariantKey] = useState(null);
  const canUseVariants =
    isVariantsEligible &&
    (!editing ||
      (product?.backendType === "single" &&
        !product?.isVariantParent &&
        !product?.parentProductId));
  useIcons();

  useEffect(() => {
    if (
      existingVariantKey &&
      !variantRows.some((variant) => variant.key === existingVariantKey)
    ) {
      setExistingVariantKey(null);
    }
  }, [existingVariantKey, variantRows]);

  const toggleVariantMode = () => {
    const next = !variantMode;
    setVariantMode(next);
    if (next) {
      setForm((current) => ({ ...current, type: "Produits finis" }));
    }
    if (!next) {
      setVariantAxes([{ name: "Taille", values: [] }]);
      setVariantRows([]);
      setExistingVariantKey(null);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* ---- photo du produit (caméra ou galerie) ---- */
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null); // objectURL local, à révoquer
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onPickFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast && toast("Veuillez choisir une image", "alert");
      return;
    }
    let out = file;
    try { out = await compressImage(file, { maxSize: 1600, quality: 0.8 }); } catch { out = file; }
    if (out.size > 10 * 1024 * 1024) {
      toast && toast("Image trop volumineuse (max 10 Mo)", "alert");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(out));
    setForm((f) => ({ ...f, imageFile: out, removeImage: false }));
    if (fileRef.current) fileRef.current.value = ""; // re-sélection du même fichier possible
  };
  const onRemovePhoto = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setForm((f) => ({ ...f, imageFile: null, image: "", removeImage: true }));
  };
  const photoSrc =
    preview || (!form.removeImage && form.image ? resolveImg(form.image) : null);

  const cost = parseProductNumber(form.cost) || 0;
  const sale = parseProductNumber(form.sale) || 0;
  const marge = sale - cost;
  const margePct = sale ? Math.round((marge / sale) * 100) : 0;
  const unitShort = (units.find((unit) => unit.name === form.unit) || {}).short || form.unit;

  /* ---- feuilles de sélection ---- */
  const sheetConfig = {
    cat: { title: "Catégorie", items: categories, sel: form.cat, add: "Nouvelle catégorie", key: "cat" },
    unit: { title: "Unité de mesure", items: units.map((u) => ({ name: u.name, sub: u.short, ico: "ruler" })), sel: form.unit, add: "Nouvelle unité", key: "unit" },
    wh: { title: "Établissement", items: warehouses.map((w) => ({ name: w.name, sub: w.sub, ico: "warehouse" })), sel: form.wh, key: "wh" },
    tax: { title: "Taxe applicable", items: taxes.map((t) => ({ name: t.name, ico: "percent" })), sel: form.tax, key: "tax" },
  };
  const cfg = sheet ? sheetConfig[sheet] : null;
  const onPick = (v) => {
    if (v === "__add__") {
      toast && toast("Créez ce référentiel depuis son écran dédié", "alert");
      setSheet(null);
      return;
    }
    set(cfg.key, v);
    setSheet(null);
  };

  /* ---- groupes de champs ---- */
  const variantToggle = canUseVariants ? (
    <button
      type="button"
      className={"vtoggle" + (variantMode ? " on" : "")}
      onClick={toggleVariantMode}
    >
      <div className="vtoggle__ico">
        <Ico name="layers" />
      </div>
      <div className="vtoggle__txt">
        <span className="vtoggle__t">
          {editing ? "Convertir en produit à variantes" : "Produit à variantes"}
        </span>
        <span className="vtoggle__s">
          {editing
            ? "Le produit actuel conservera son stock, ses codes et son historique."
            : "Décliner en plusieurs versions (Taille, Couleur…), chacune avec son stock et son prix."}
        </span>
      </div>
      <span className={"vtoggle__sw" + (variantMode ? " on" : "")}>
        <span className="vtoggle__knob" />
      </span>
    </button>
  ) : null;

  const groupIdentity = (
    <>
      <Field label="Photo du produit" hint="caméra ou galerie">
        {photoSrc ? (
          <div className="uptile" style={{ padding: 0, overflow: "hidden" }}>
            <img src={photoSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        ) : (
          <button type="button" className="uptile" onClick={() => fileRef.current?.click()}>
            <Ico name="camera" />Ajouter une photo
            <small>Appareil photo ou galerie · JPG/PNG · max 10 Mo</small>
          </button>
        )}
        {photoSrc ? (
          <div className="frow" style={{ marginTop: 8 }}>
            <button type="button" className="btn btn--ghost btn--flex" onClick={() => fileRef.current?.click()}>
              <Ico name="camera" />Changer
            </button>
            <button type="button" className="btn btn--ghost btn--flex" onClick={onRemovePhoto}>
              <Ico name="trash-2" />Supprimer
            </button>
          </div>
        ) : null}
        <input ref={fileRef} type="file" accept="image/*" hidden
          onChange={(e) => onPickFile(e.target.files?.[0])} />
      </Field>
      <Field label="Nom du produit" req>
        <Text value={form.name} onChange={(v) => set("name", v)} placeholder="Ex : Ciment CIMFASO 50kg" />
      </Field>
      <Field label="Type de produit" req>
        <Seg
          value={form.type}
          opts={TYPE_OPTS}
          onChange={(v) => set("type", v)}
          disabled={variantMode}
        />
      </Field>
      <Field label="Code article" req hint={editing && variantMode ? "conservé" : "auto"}>
        <Text
          value={form.code}
          onChange={(v) => set("code", v)}
          placeholder="CIM-50"
          disabled={editing && variantMode}
        />
      </Field>
      <Field label="Catégorie" req>
        <SelectBtn value={form.cat} placeholder="Choisir une catégorie" leadIco="tag" onOpen={() => setSheet("cat")} />
      </Field>
      <Field label="Description">
        <textarea className="ctrl" rows={2} placeholder="Note interne, caractéristiques…"
          value={form.desc} onChange={(e) => set("desc", e.target.value)} />
      </Field>
      <Disclosure icon="sliders-horizontal" label="Champs avancés">
        <Field label="Slug (boutique en ligne)">
          <div className="input-wrap">
            <input className="ctrl" placeholder="ciment-cimfaso-50kg" value={form.slug}
              onChange={(e) => set("slug", e.target.value)} />
            <span className="suffix" style={{ pointerEvents: "auto", color: "var(--blue)" }}
              onClick={() => set("slug", genSlug(form.name))}>Générer</span>
          </div>
        </Field>
        <Field label="Type de code-barres">
          <Seg value={form.bcSymbol} opts={BARCODE_OPTS} onChange={(v) => set("bcSymbol", v)} />
        </Field>
        <Field label="Code-barres">
          <div className="input-wrap">
            <input className="ctrl" inputMode="numeric" placeholder="618110…" value={form.bc}
              disabled={editing && variantMode}
              onChange={(e) => set("bc", e.target.value)} />
            <span
              className="suffix"
              style={{
                pointerEvents: editing && variantMode ? "none" : "auto",
                color: editing && variantMode ? "var(--muted)" : "var(--blue)",
              }}
              onClick={() => {
                set("bc", "618110" + String(Math.floor(Math.random() * 1e7)).padStart(7, "0"));
                toast && toast("Code-barres généré");
              }}
            >
              {editing && variantMode ? "Conservé" : "Générer"}
            </span>
          </div>
        </Field>
      </Disclosure>
    </>
  );

  const groupStock = (
    <>
      <Field label="Gestion du stock" req>
        <Seg value={form.stockMode} opts={STOCK_MODE_OPTS} onChange={(v) => set("stockMode", v)} />
      </Field>
      <Field label="Établissement" req>
        <SelectBtn value={form.wh} placeholder="Sélectionner un établissement" leadIco="warehouse" onOpen={() => setSheet("wh")} />
      </Field>
      <div className="frow">
        <Field label={editing ? "Stock actuel" : "Stock initial"}>
          <Text value={form.stock} onChange={(v) => set("stock", v)} placeholder="0" mode={isRawMaterial ? "decimal" : "numeric"}
            suffix={form.unit ? unitShort : "qté"} disabled={editing} />
        </Field>
        <Field label="Unité" req>
          <SelectBtn value={form.unit} placeholder="Unité" leadIco="ruler" onOpen={() => setSheet("unit")} />
        </Field>
      </div>
      <Field label="Alerte de stock bas" hint="notification">
        <Text value={form.alert} onChange={(v) => set("alert", v)} placeholder="Ex : 30" mode={isRawMaterial ? "decimal" : "numeric"} suffix={unitShort || "seuil"} />
      </Field>
      <Disclosure icon="boxes" label="Conditionnement d'achat (carton / sac…)">
        <Field label="Libellé du conditionnement">
          <Text value={form.pkLabel} onChange={(v) => set("pkLabel", v)} placeholder="Ex : Palette de 40 sacs" />
        </Field>
        <div className="frow">
          <Field label="Contenu">
            <Text value={form.pkContent} onChange={(v) => set("pkContent", v)} placeholder="40" mode="decimal" suffix={unitShort || "u"} />
          </Field>
          <Field label="Prix d'achat lot">
            <Text value={form.pkPrice} onChange={(v) => set("pkPrice", v)} placeholder="Ex : 152000" mode="decimal" suffix="CFA" />
          </Field>
        </div>
      </Disclosure>
    </>
  );

  const groupPrice = (
    <>
      <div className="frow">
        <Field label="Prix d'achat">
          <Text value={form.cost} onChange={(v) => set("cost", v)} placeholder="0" mode={isRawMaterial ? "decimal" : "numeric"} suffix="CFA" />
        </Field>
        <Field label={priceLabel} req>
          <Text value={form.sale} onChange={(v) => set("sale", v)} placeholder="0" mode={isRawMaterial ? "decimal" : "numeric"} suffix="CFA" />
        </Field>
      </div>
      {!isRawMaterial && <div className="marge">
        <span className="marge__l">Marge unitaire</span>
        <span className="marge__v">{fmt(marge)} <small>F · {margePct} %</small></span>
      </div>}
      <Field label="Taxe applicable">
        <SelectBtn value={form.tax} placeholder="Sélectionner une taxe" leadIco="percent" onOpen={() => setSheet("tax")} />
      </Field>
    </>
  );

  const groupHeader = (ico, title, sub) => (
    <div className="fgroup__h">
      <div className="ico"><Ico name={ico} /></div>
      <div><h3>{title}</h3><p>{sub}</p></div>
    </div>
  );

  // Champs partagés par toutes les déclinaisons (entrepôt, unité, taxe).
  const groupVariantShared = (
    <>
      <Field label="Établissement" req hint={editing && variantMode ? "conservé" : undefined}>
        <SelectBtn
          value={form.wh}
          placeholder="Sélectionner un établissement"
          leadIco="warehouse"
          disabled={editing && variantMode}
          onOpen={() => setSheet("wh")}
        />
      </Field>
      <div className="frow">
        <Field label="Unité" req>
          <SelectBtn value={form.unit} placeholder="Unité" leadIco="ruler" onOpen={() => setSheet("unit")} />
        </Field>
        <Field label="Taxe applicable">
          <SelectBtn value={form.tax} placeholder="Taxe" leadIco="percent" onOpen={() => setSheet("tax")} />
        </Field>
      </div>
      <Field label="Gestion du stock" req>
        <Seg value={form.stockMode} opts={STOCK_MODE_OPTS} onChange={(v) => set("stockMode", v)} />
      </Field>
    </>
  );

  const variantDefaults = () => ({
    purchase_price: Number(form.cost) || 0,
    sales_price: Number(form.sale) || 0,
    stock_quantitiy_alert: Number(form.alert) || 0,
    tax_id: (taxes.find((t) => t.name === form.tax) || {}).id ?? null,
  });

  /* ---- corps ---- */
  let body;
  if (variantMode) {
    body = (
      <>
        <div className="fgroup">
          {variantToggle}
          {groupHeader("tag", "Identité du produit", "Nom, catégorie, base commune")}
          {groupIdentity}
        </div>
        <div className="fgroup">
          {groupHeader("boxes", "Paramètres partagés", "Établissement, unité et taxe des déclinaisons")}
          {groupVariantShared}
        </div>
        <VariantEditorMobile
          axes={variantAxes}
          setAxes={setVariantAxes}
          variants={variantRows}
          setVariants={setVariantRows}
          getDefaults={variantDefaults}
          toast={toast}
          existingProduct={
            editing
              ? {
                  name: product.name,
                  item_code: product.code,
                  barcode_value: product.bc || product.code,
                  current_stock: Number(product.stock) || 0,
                }
              : null
          }
          existingVariantKey={existingVariantKey}
          onExistingVariantChange={setExistingVariantKey}
        />
      </>
    );
  } else if (view === "page") {
    body = (
      <>
        <div className="fgroup">{variantToggle}{groupHeader("tag", "Identité", "Nom, code, catégorie")}{groupIdentity}</div>
        <div className="fgroup">{groupHeader("boxes", "Stock & unité", "Établissement, quantité, alerte")}{groupStock}</div>
        <div className="fgroup">{groupHeader("banknote", "Prix & taxe", isRawMaterial ? "Achat et entrée en production" : "Achat, vente, marge")}{groupPrice}</div>
      </>
    );
  } else if (step === 0) {
    body = <div className="fgroup">{variantToggle}{groupHeader("tag", "Identité du produit", "Ce qui le rend reconnaissable")}{groupIdentity}</div>;
  } else if (step === 1) {
    body = <div className="fgroup">{groupHeader("boxes", "Stock & unité", "Quantités et seuil d'alerte")}{groupStock}</div>;
  } else if (step === 2) {
    body = <div className="fgroup">{groupHeader("banknote", "Prix & taxe", isRawMaterial ? "Prix par unité de stock" : "Calcul de la marge en direct")}{groupPrice}</div>;
  } else {
    body = <Review form={form} marge={marge} onGoto={setStep} units={units} />;
  }

  /* ---- validation + enregistrement ---- */
  const validateStep = () => {
    if (view === "page") {
      if (!form.name || !form.cat || !form.wh || !form.unit || !form.sale) {
        toast && toast(`Nom, catégorie, établissement, unité et ${priceLabel.toLowerCase()} requis`, "alert");
        return false;
      }
      return true;
    }
    if (step === 0 && (!form.name || !form.cat)) {
      toast && toast("Le nom et la catégorie sont requis", "alert");
      return false;
    }
    if (step === 1 && (!form.wh || !form.unit)) {
      toast && toast("L'établissement et l'unité sont requis", "alert");
      return false;
    }
    if (step === 2 && !form.sale) { toast && toast(`Indiquez le ${priceLabel.toLowerCase()}`, "alert"); return false; }
    return true;
  };
  const handleSave = () => {
    if (!form.name || !form.cat || !form.wh || !form.unit || !form.sale) {
      toast && toast(`Nom, catégorie, établissement, unité et ${priceLabel.toLowerCase()} requis`, "alert");
      return;
    }
    onSave({ ...form, code: form.code || genCode(form.name) }, editing ? product.id : null);
  };
  const handleSaveVariants = () => {
    if (!form.name || !form.cat || !form.wh || !form.unit) {
      toast && toast("Nom, catégorie, établissement et unité sont requis", "alert");
      return;
    }
    if (variantRows.length < (editing ? 2 : 1)) {
      toast && toast("Générez les déclinaisons avant d'enregistrer", "alert");
      return;
    }
    const existingVariant = editing
      ? variantRows.find((variant) => variant.key === existingVariantKey)
      : null;
    if (editing && !existingVariant) {
      toast &&
        toast(
          "Choisissez la déclinaison correspondant au produit actuel",
          "alert"
        );
      return;
    }
    const missingPrice = variantRows.find(
      (v) => v.sales_price === "" || v.sales_price === null || v.sales_price === undefined
    );
    if (missingPrice) {
      toast && toast(`Prix de vente manquant pour « ${missingPrice.label} »`, "alert");
      return;
    }
    if (
      editing &&
      typeof window !== "undefined" &&
      !window.confirm(
        "Convertir définitivement ce produit ? Son stock et son historique seront conservés dans la déclinaison choisie, mais la reconversion automatique en produit simple ne sera pas disponible."
      )
    ) {
      return;
    }
    onSaveVariants &&
      onSaveVariants({
        form: { ...form, code: form.code || "" },
        axes: variantAxes,
        rows: variantRows,
        existingProductId: editing ? product.id : null,
        existingVariantValues: existingVariant?.variant_values || null,
      });
  };
  const next = () => { if (validateStep()) { setStep((s) => s + 1); scrollTop(); } };
  const prev = () => { setStep((s) => Math.max(0, s - 1)); scrollTop(); };

  /* ---- CTA ---- */
  let cta;
  if (variantMode) {
    cta = (
      <>
        <button className="btn btn--ghost" style={{ flex: "0 0 auto" }} onClick={onClose}><Ico name="x" /></button>
        <button className="btn btn--primary btn--flex" onClick={handleSaveVariants} disabled={saving}>
          <Ico name={saving ? "loader-circle" : "check"} />
          {saving
            ? "Enregistrement…"
            : editing
            ? "Convertir en produit à variantes"
            : "Créer le produit à variantes"}
        </button>
      </>
    );
  } else if (view === "page") {
    cta = (
      <>
        <button className="btn btn--ghost" style={{ flex: "0 0 auto" }} onClick={onClose}><Ico name="x" /></button>
        <button className="btn btn--primary btn--flex" onClick={handleSave} disabled={saving}>
          <Ico name={saving ? "loader-circle" : "check"} />{saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer le produit"}
        </button>
      </>
    );
  } else if (step === 0) {
    cta = (
      <>
        <button className="btn btn--ghost" style={{ flex: "0 0 auto" }} onClick={onClose}><Ico name="x" /></button>
        <button className="btn btn--primary btn--flex" onClick={next}>Continuer<Ico name="arrow-right" /></button>
      </>
    );
  } else if (step < STEPS.length) {
    cta = (
      <>
        <button className="btn btn--ghost" style={{ flex: "0 0 auto" }} onClick={prev}><Ico name="arrow-left" /></button>
        <button className="btn btn--primary btn--flex" onClick={next}>
          {step === STEPS.length - 1 ? "Vérifier" : "Continuer"}<Ico name="arrow-right" />
        </button>
      </>
    );
  } else {
    cta = (
      <>
        <button className="btn btn--ghost" style={{ flex: "0 0 auto" }} onClick={prev}><Ico name="arrow-left" /></button>
        <button className="btn btn--primary btn--flex" onClick={handleSave} disabled={saving}>
          <Ico name={saving ? "loader-circle" : "check"} />{saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer le produit"}
        </button>
      </>
    );
  }

  return (
    <div className="screen screen--in product-editor-mobile">
      <div className="form-top">
        <div className="form-top__row">
          <button className="iconbtn" onClick={onClose}><Ico name="x" /></button>
          <h1>
            {editing && variantMode
              ? "Convertir en variantes"
              : editing
              ? isRawMaterial ? "Modifier la matière première" : "Modifier le produit"
              : variantMode
              ? "Produit à variantes"
              : isRawMaterial ? "Nouvelle matière première" : "Nouveau produit"}
          </h1>
          {variantMode ? null : (
            <div className="vswitch">
              <button className={view === "wizard" ? "on" : ""} onClick={() => setView("wizard")}>
                <Ico name="list-ordered" />Étapes
              </button>
              <button className={view === "page" ? "on" : ""} onClick={() => { setView("page"); setStep(0); }}>
                <Ico name="scroll-text" />Page
              </button>
            </div>
          )}
        </div>
        {variantMode ? (
          <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
            {editing
              ? "Associez le produit actuel à une déclinaison, puis créez les autres versions."
              : "Définissez la base commune puis les déclinaisons (chacune a son stock et son prix)."}
          </div>
        ) : view === "wizard" ? (
          <Stepper step={step} />
        ) : (
          <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
            Toutes les sections sur une page — repliez ce qui ne vous concerne pas.
          </div>
        )}
      </div>

      <div className="fbody" id="eapFbody">{referenceNotice}{body}</div>
      <div className="fcta">{cta}</div>

      {cfg ? (
        <OptionSheet
          open={!!sheet} title={cfg.title} items={cfg.items} selected={cfg.sel}
          addLabel={cfg.add} onPick={onPick} onClose={() => setSheet(null)}
        />
      ) : null}
    </div>
  );
}

function scrollTop() {
  const el = document.getElementById("eapFbody");
  if (el) el.scrollTop = 0;
}

function Stepper({ step }) {
  return (
    <div className="stepper">
      {STEPS.map((s, i) => {
        const cls = i < step ? "done" : i === step ? "active" : "";
        return (
          <div className={"step " + cls} key={s.key}>
            <div className="step__dot">
              {i < step ? <Ico name="check" style={{ width: 14, height: 14 }} /> : i + 1}
            </div>
            <div className="step__lbl">{s.label}</div>
            {i < STEPS.length - 1 ? <div className="step__bar" /> : null}
          </div>
        );
      })}
    </div>
  );
}

function Disclosure({ icon, label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"disclose" + (open ? " open" : "")}>
      <button className="disclose__h" onClick={() => setOpen((o) => !o)}>
        <Ico name={icon} className="lead" />{label}<Ico name="chevron-right" className="chev" />
      </button>
      <div className="disclose__b">{children}</div>
    </div>
  );
}

function Review({ form, marge, onGoto, units }) {
  const sale = parseProductNumber(form.sale) || 0;
  const isRawMaterial = form.type === "Matières premières";
  const displayNumber = isRawMaterial ? (value) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 }).format(parseProductNumber(value)) : fmt;
  const unitShort = (units.find((unit) => unit.name === form.unit) || {}).short || form.unit;
  const row = (l, v, step) => (
    <div className="review__row">
      <span className="rl">{l}</span>
      <span className="rv">{v || <span style={{ color: "var(--muted-2)" }}>—</span>}</span>
      {step != null ? <button className="review__edit" onClick={() => onGoto(step)}>Modifier</button> : null}
    </div>
  );
  return (
    <>
      <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600, marginBottom: 12 }}>
        Vérifiez avant d'enregistrer.
      </div>
      <div className="review">
        {row("Nom", form.name, 0)}
        {row("Type", form.type, 0)}
        {row("Code", form.code, 0)}
        {row("Catégorie", form.cat, 0)}
        {row("Stock", form.stock ? `${displayNumber(form.stock)} ${unitShort}` : "", 1)}
        {row("Gestion", labelOfMode(form.stockMode), 1)}
        {row("Alerte", form.alert ? `${displayNumber(form.alert)} ${unitShort}` : "", 1)}
        {row(isRawMaterial ? "Prix d’entrée en production" : "Prix de vente", form.sale !== "" ? `${displayNumber(sale)} CFA` : "", 2)}
        {!isRawMaterial && row("Marge", sale ? `${fmt(marge)} F` : "", 2)}
      </div>
    </>
  );
}
function labelOfMode(v) {
  return (STOCK_MODE_OPTS.find((o) => o.v === v) || {}).label || v;
}
